<template>
  <div class="modal fade" id="printReportModal" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog modal-sm">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Print Report</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body text-start">
          <div
            v-for="opt in sectionOptions"
            :key="opt.key"
            class="form-check mb-2"
          >
            <input
              class="form-check-input"
              type="checkbox"
              :id="`print-report-${opt.key}`"
              :checked="sections[opt.key]"
              :disabled="!opt.available || exporting"
              @change="toggleSection(opt.key, $event.target.checked)"
            />
            <label class="form-check-label" :for="`print-report-${opt.key}`">
              {{ opt.label }}
              <span v-if="!opt.available" class="text-muted small">— no data</span>
            </label>
          </div>
          <hr class="my-2" />
          <div class="form-check">
            <input
              class="form-check-input"
              type="checkbox"
              id="print-report-select-all"
              :checked="allSelected"
              :disabled="exporting"
              @change="toggleAll($event.target.checked)"
            />
            <label class="form-check-label" for="print-report-select-all">Select all</label>
          </div>
          <div v-if="errorMsg" class="alert alert-danger mt-3 py-2 small mb-0">{{ errorMsg }}</div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-primary" :disabled="exporting || !anySelected" @click="exportPdf">
            <span v-if="exporting" class="spinner-border spinner-border-sm me-1"></span>
            Ok
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Modal } from 'bootstrap';
import { inject } from 'vue';
import { mapGetters } from 'vuex';
import { runCapture, hasCapture } from '../report/captureRegistry';
import { buildReportPdf } from '../report/buildReportPdf';

export default {
  name: 'PrintReportModal',
  setup() {
    // Модалка живёт внутри NavBar, поэтому provide из него доступны напрямую.
    const stat = inject('stat', null);
    return { stat };
  },
  props: {
    // Текущий Site из панели Option ("Choose site..." — плейсхолдер, не сайт).
    siteName: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      modal: null,
      exporting: false,
      errorMsg: '',
      // Реестр захвата (captureRegistry) — не реактивный. Бампаем счётчик при
      // открытии модалки, чтобы computed-доступность секций пересчиталась с
      // учётом уже зарегистрированных карты/графиков.
      openTick: 0,
    };
  },
  computed: {
    ...mapGetters('report', ['reportSections']),
    ...mapGetters(['paramTitles', 'stateDateStart', 'stateDateEnd']),
    sections() {
      return this.reportSections;
    },
    site() {
      const s = this.siteName;
      return s && s !== 'Choose site...' ? s : '';
    },
    mapAvailable() {
      // openTick — реактивная зависимость, форсит пересчёт после open().
      return this.openTick >= 0 && hasCapture('map');
    },
    statsAvailable() {
      const s = this.stat;
      return !!s && ['r', 'g', 'b'].some(ch => s[ch] && s[ch].stats);
    },
    chartsAvailable() {
      const enabled = this.$store.getters['chartsParams/chartsEnabledParameters'] || [];
      return enabled.length > 0 || (this.openTick >= 0 && hasCapture('charts'));
    },
    profilingAvailable() {
      const p = this.$store.getters['profilingParams/profilingSelectedPoint'];
      return !!p && ((Array.isArray(p.profiling_ch3) && p.profiling_ch3.length) ||
                     (Array.isArray(p.profiling) && p.profiling.length));
    },
    sectionOptions() {
      return [
        { key: 'map', label: 'Map', available: this.mapAvailable },
        { key: 'charts', label: 'Charts', available: this.chartsAvailable },
        { key: 'profiling', label: 'Profiling', available: this.profilingAvailable },
      ];
    },
    anySelected() {
      return this.sectionOptions.some(o => o.available && this.sections[o.key]);
    },
    allSelected() {
      const avail = this.sectionOptions.filter(o => o.available);
      return avail.length > 0 && avail.every(o => this.sections[o.key]);
    },
  },
  mounted() {
    this.modal = new Modal(document.getElementById('printReportModal'));
  },
  methods: {
    open() {
      this.errorMsg = '';
      this.openTick += 1;
      this.modal.show();
    },
    close() {
      this.modal.hide();
    },
    toggleSection(key, value) {
      this.$store.dispatch('report/setReportSection', { key, value });
    },
    toggleAll(value) {
      this.sectionOptions.forEach((o) => {
        if (o.available) this.toggleSection(o.key, value);
      });
    },
    fmtRange(a, b) {
      if (!a && !b) return '—';
      return `${a || '…'} — ${b || '…'}`;
    },
    statsData() {
      const s = this.stat;
      if (!s) return null;
      const fmt = v => (v == null ? '' : Number(v).toFixed(2));
      const titles = this.paramTitles || [];
      const rows = [];
      [['r', 0], ['g', 1], ['b', 2]].forEach(([ch, i]) => {
        const c = s[ch];
        if (c && c.stats) {
          rows.push({
            label: titles[i] || ch.toUpperCase(),
            min: fmt(c.stats.min), max: fmt(c.stats.max),
            avg: fmt(c.stats.avg), rms: fmt(c.stats.std),
            count: c.count != null ? c.count : '',
          });
        }
      });
      return rows.length ? { startDate: s.start_date, endDate: s.end_date, rows } : null;
    },
    profilingMeta() {
      const p = this.$store.getters['profilingParams/profilingSelectedPoint'];
      if (!p) return [];
      const ts = p.ts ? new Date(p.ts) : null;
      // Координаты округляем так же, как profilingFormatCoord в MapContainer.
      const coord = v => (v == null ? '—' : Number(v).toFixed(6));
      return [
        { label: 'Datetime', value: ts && !Number.isNaN(ts.getTime()) ? ts.toISOString().replace('T', ' ').replace(/\.\d+Z$/, ' UTC') : (p.ts || '') },
        { label: 'Latitude', value: coord(p.lat) },
        { label: 'Longitude', value: coord(p.lon) },
      ];
    },
    async exportPdf() {
      this.errorMsg = '';
      this.exporting = true;
      try {
        const sec = this.sections;
        const report = {
          site: this.site,
          meta: {
            generatedAt: new Date().toLocaleString(),
            timeRange: this.fmtRange(this.stateDateStart, this.stateDateEnd),
          },
        };

        if (sec.map && hasCapture('map')) {
          report.map = await runCapture('map');
          // Statistics — часть секции Map, идёт таблицей под картой.
          report.stats = this.statsData();
        }
        if (sec.charts && this.chartsAvailable) {
          report.charts = (await runCapture('charts')) || [];
        }
        if (sec.profiling && this.profilingAvailable) {
          report.profiling = {
            images: (await runCapture('profiling')) || [],
            meta: this.profilingMeta(),
          };
        }

        await buildReportPdf(report);
        this.close();
      } catch (e) {
        console.error('Print report export failed:', e);
        this.errorMsg = 'Failed to generate PDF. See console for details.';
      } finally {
        this.exporting = false;
      }
    },
  },
};
</script>
